/**
 * Client-side API wrapper for the PhishTrainer PLAY portal (learner realm).
 *
 * Mirrors the admin `ptFetch` pattern (lib/phishtrainer/admin-api.ts) but for
 * the recipient/portal realm: reads the learner's recipient JWT from
 * localStorage and calls the backend `/v1/phishtrainer/play/*` endpoints
 * DIRECTLY — no Next.js proxy route, no httpOnly cookie.
 *
 * Why localStorage instead of the `pt_player_session` httpOnly cookie:
 * the cookie (SameSite=lax; Secure) is dropped by incognito / Safari-Brave ITP
 * / third-party-cookie blocking, which bounced learners back to the login page
 * intermittently. The admin app already uses the robust localStorage-token
 * pattern; the player now shares it. The redeem/SSO flow still ALSO sets the
 * cookie for backward-compat with any not-yet-migrated proxy route.
 *
 * Tokens are stored under `hf-play:*` keys (distinct from the admin
 * `hf-app:*` keys) so an operator and a learner can coexist in one browser.
 */
import { API_CONFIG } from '@/lib/api/config'

export const PLAY_KEYS = {
  ACCESS: 'hf-play:access-token',
  REFRESH: 'hf-play:refresh-token',
  USER: 'hf-play:user',
} as const

/** The learner identity the player UI renders. Frozen shape — matches what the
 *  `/api/play/auth/verify` + `/session` routes have always returned. */
export interface PlayUser {
  id: string
  email?: string
  name: string
  /** workspaceId — kept as `orgId` for FE back-compat. */
  orgId: string
  isAnonymous: boolean
  isManager?: boolean
  isImpersonating?: boolean
}

interface RedeemResponse {
  accessToken: string
  refreshToken?: string
  expiresIn?: number
  recipient: {
    id: string
    email?: string | null
    firstName?: string | null
    lastName?: string | null
    workspaceId: string
    companyId?: string
    domainId?: string | null
    isManager?: boolean
  }
}

function beUrl(path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`
  return `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}${clean}`
}

// ── Session storage (localStorage) ────────────────────────────────────────────

export function getPlayToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(PLAY_KEYS.ACCESS)
}

export function getPlayUser(): PlayUser | null {
  if (typeof window === 'undefined') return null
  const raw = localStorage.getItem(PLAY_KEYS.USER)
  if (!raw) return null
  try {
    return JSON.parse(raw) as PlayUser
  } catch {
    return null
  }
}

/** Persist the learner session from a redeem/refresh payload. Returns the
 *  derived PlayUser the provider renders. */
export function setPlaySession(data: RedeemResponse): PlayUser {
  const r = data.recipient
  const user: PlayUser = {
    id: r.id,
    email: r.email ?? undefined,
    name: `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() || r.email || 'Learner',
    orgId: r.workspaceId,
    isAnonymous: false,
    isManager: r.isManager,
  }
  if (typeof window !== 'undefined') {
    localStorage.setItem(PLAY_KEYS.ACCESS, data.accessToken)
    if (data.refreshToken) localStorage.setItem(PLAY_KEYS.REFRESH, data.refreshToken)
    localStorage.setItem(PLAY_KEYS.USER, JSON.stringify(user))
  }
  return user
}

/** Store just the user object (e.g. from a server-set-cookie flow that already
 *  returned the user but not tokens). */
export function setPlayUser(user: PlayUser): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(PLAY_KEYS.USER, JSON.stringify(user))
  }
}

/** Store raw tokens (used by the verify route bridge + SSO callback). */
export function setPlayTokens(accessToken: string, refreshToken?: string): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(PLAY_KEYS.ACCESS, accessToken)
  if (refreshToken) localStorage.setItem(PLAY_KEYS.REFRESH, refreshToken)
}

export function clearPlaySession(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(PLAY_KEYS.ACCESS)
  localStorage.removeItem(PLAY_KEYS.REFRESH)
  localStorage.removeItem(PLAY_KEYS.USER)
}

// ── Redeem (public — the invite link entry) ───────────────────────────────────

/** Redeem an invite token directly against the public BE redeem endpoint and
 *  persist the resulting learner session in localStorage. */
export async function redeemInvite(token: string): Promise<PlayUser> {
  const res = await fetch(beUrl('/phishtrainer/play/auth/redeem'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
    cache: 'no-store',
  })
  if (!res.ok) throw new Error('Invalid or expired invite link')
  const data = (await res.json()) as RedeemResponse
  return setPlaySession(data)
}

// ── Authenticated play fetch (recipient JWT → BE /v1/phishtrainer/play/*) ──────

/** Rotate the recipient JWT via the portal refresh endpoint. Returns the fresh
 *  access token, or null when refresh is impossible (caller signs out). */
async function refreshPlayToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null
  const refreshToken = localStorage.getItem(PLAY_KEYS.REFRESH)
  if (!refreshToken) return null
  try {
    const res = await fetch(beUrl('/portal/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      cache: 'no-store',
    })
    if (!res.ok) return null
    const data = (await res.json()) as { accessToken: string; refreshToken?: string }
    setPlayTokens(data.accessToken, data.refreshToken)
    return data.accessToken
  } catch {
    return null
  }
}

/**
 * Call a same-origin Next.js play proxy route (e.g. `/api/phishtrainer/library`)
 * but forward the learner's hf-play bearer so the proxy authenticates via the
 * localStorage token instead of the pt_player_session cookie (incognito/ITP
 * safe). The proxy keeps doing its prototype-shape transform; only the auth
 * source changes. Use this for the proxy routes that still do server-side
 * mapping (library / knowledge / gamification / history / taxonomy / settings /
 * rounds); use playFetch() for routes that hit the BE play API 1:1.
 */
export function pfetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getPlayToken()
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  }
  if (token) headers.Authorization = `Bearer ${token}`
  return fetch(path, { ...init, headers })
}

export class PlayAuthError extends Error {}

/**
 * Authenticated fetch against a `/v1/phishtrainer/play/...` endpoint. `path` is
 * appended to the play prefix unless it begins with `/`. Attaches the learner's
 * Bearer token; on 401 it refreshes once and retries. Returns the raw Response
 * so callers can map the body / handle non-JSON.
 */
export async function playRawFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = path.startsWith('/')
    ? beUrl(path)
    : beUrl(`/phishtrainer/play/${path}`)
  const doFetch = (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined ?? {}),
    }
    if (token) headers.Authorization = `Bearer ${token}`
    return fetch(url, { ...init, headers, cache: 'no-store' })
  }

  let res = await doFetch(getPlayToken())
  if (res.status === 401) {
    const fresh = await refreshPlayToken()
    if (fresh) res = await doFetch(fresh)
  }
  return res
}

/** Authenticated play fetch that returns the parsed JSON body. Throws
 *  PlayAuthError on 401 (caller should sign out / re-auth) and Error on other
 *  non-ok responses. */
export async function playFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await playRawFetch(path, init)
  if (res.status === 401) {
    clearPlaySession()
    throw new PlayAuthError('Your session expired — please sign in again.')
  }
  if (!res.ok) {
    let msg = `Request failed (${res.status})`
    try {
      const j = (await res.json()) as { message?: string; error?: string }
      msg = j.message || j.error || msg
    } catch {
      /* keep default */
    }
    throw new Error(msg)
  }
  return (await res.json()) as T
}
